import { differenceInCalendarDays } from 'date-fns';
import { REPORTS_EXPIRED_TEXT } from '../constants/applicationsConstants';
import { APPLICANT_STATUS_LABELS, APPLICATION_STATUS_LABELS } from '../constants/statuses';
import {
  ApplicantStatus,
  ApplicationStatus,
  type RentalApplication,
  type UserStatus,
  UserStatusType,
} from '../graphql/queries';
import { formatTimeToDisplayDate, pluralize } from './formatters';
import { getExpirationDate, getUserStatus } from './user-statuses';
import { getIsComplete, getIsInProgress } from './business-logic';

export function getTagAppearance(application: RentalApplication) {
  if (application.applicationStatus === ApplicationStatus.approved) {
    return 'success';
  }

  if (application.applicationStatus === ApplicationStatus.conditionally_approved) {
    return 'warning';
  }

  if (
    application.status === ApplicantStatus.completed &&
    application.applicationStatus !== ApplicationStatus.declined
  ) {
    return 'info';
  }

  return 'gray';
}

export function getTagText(application: RentalApplication) {
  if (!application.applicationStatus || !application.status) {
    return;
  }
  if (
    application.applicationStatus === ApplicationStatus.approved ||
    application.applicationStatus === ApplicationStatus.declined ||
    application.applicationStatus === ApplicationStatus.conditionally_approved
  ) {
    return APPLICATION_STATUS_LABELS[application.applicationStatus];
  }

  return APPLICANT_STATUS_LABELS[application.status];
}

function getSortByDate(application: RentalApplication): string | null | undefined {
  switch (application.applicationStatus) {
    case ApplicationStatus.approved:
      return application.invitedToMoveInDateTime;
    case ApplicationStatus.submitted:
    case ApplicationStatus.partially_submitted:
    case ApplicationStatus.declined:
      return application.submittedDateTime;
    case ApplicationStatus.withdrawn:
      return application.withdrawnDateTime;
    default:
      return application.startedDateTime;
  }
}

export function sortByDate(a: RentalApplication | null, b: RentalApplication | null) {
  if (!a || !b) {
    return 0;
  }
  const aDateToSortBy = getSortByDate(a);
  const bDateToSortBy = getSortByDate(b);

  if (!aDateToSortBy || !bDateToSortBy) {
    return 0;
  }

  return Date.parse(aDateToSortBy) < Date.parse(bDateToSortBy) ? 1 : -1;
}

export function getCreditAndBackgroundReportExpiration(userStatuses: Array<UserStatus>): string | null {
  const creditReportStatus = getUserStatus(userStatuses, UserStatusType.creditReport);
  const backgroundReportStatus = getUserStatus(userStatuses, UserStatusType.backgroundReport);

  const expiration = getExpirationDate(creditReportStatus) || getExpirationDate(backgroundReportStatus);

  if (!expiration) {
    return null;
  }

  const daysUntilExpiration = differenceInCalendarDays(expiration, new Date());
  if (daysUntilExpiration < 0) {
    return REPORTS_EXPIRED_TEXT;
  }

  if (daysUntilExpiration == 0) {
    return 'Credit and background reports expire today';
  }

  return `Credit and background reports expire in ${pluralize('day', daysUntilExpiration)}`;
}

export function getStartedByTextWithDate(application: RentalApplication) {
  const invitedDateTime = application.invitedDateTime || '';
  const startedDateTime = application.startedDateTime || '';
  const invitedOrStartedTime = invitedDateTime || startedDateTime;
  const startedByText = application.startedByText || '';

  const isInProgress = getIsInProgress(application);
  const isSubmitted = getIsComplete(application);

  if (isInProgress) {
    const formattedStartDate = formatTimeToDisplayDate(invitedOrStartedTime);
    return `${startedByText}${formattedStartDate ? ` on ${formattedStartDate}` : ''}`;
  } else if (isSubmitted) {
    const formattedSubmittedDate = formatTimeToDisplayDate(application.submittedDateTime || '');
    const sharedWithLandlordEmail = application.applicationShareInformation?.landlordEmail;
    const sharedWithLandlordString = sharedWithLandlordEmail ? ` to ${sharedWithLandlordEmail} ` : '';
    return `You sent this application${sharedWithLandlordString}${formattedSubmittedDate ? ` on ${formattedSubmittedDate}` : ''}`;
  }

  return '';
}
